import { useState, useEffect, useRef, useCallback } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, ArrowLeft, ArrowRight, Calendar as CalendarIcon, Clock, User, X, FileText, Upload, Trash2 } from "lucide-react";
import { SALON_POLICIES } from "@/data/policies";
import { bookableServices } from "@/data/serviceCatalog";
import { fetchApi } from "@/lib/apiBase";
import { parseDurationMinutes } from "@/lib/duration";
const MIN_BOOKING_NOTICE_HOURS = 48;
const DEFAULT_BOOKING_SETTINGS = {
  min_notice_hours: 48,
  max_advance_days: 120,
  weekly_hours: [
    { day_key: "monday", enabled: true },
    { day_key: "tuesday", enabled: true },
    { day_key: "wednesday", enabled: true },
    { day_key: "thursday", enabled: true },
    { day_key: "friday", enabled: true },
    { day_key: "saturday", enabled: true },
    { day_key: "sunday", enabled: false },
  ],
};
const SEW_IN_CUTOFF_SERVICES = new Set([
  "basic frontal sew in package",
  "basic closure sew in package",
  "basic traditional sew in package",
  "deluxe frontal sew in package",
  "deluxe sew in package",
  "deluxe closure sew in package",
  "basic flipover sew in package",
  "deluxe flipover sew in package",
]);

// Mirrors _is_sew_in_service on the server. The server is what enforces the
// cutoff; this only decides whether to say so up front.
const isSewInService = (name) =>
  SEW_IN_CUTOFF_SERVICES.has(String(name || "").trim().toLowerCase());

const NO_TIMES_AVAILABLE_MESSAGE = "This date is fully booked or unavailable. Please choose another day.";
const AVAILABILITY_FETCH_ERROR_MESSAGE = "We couldn't load times right now. Please try again in a moment.";
const EMAIL_QUOTE_CHARS_REGEX = /["'`‘’“”]/g;

function extractApiErrorMessage(payload, fallback = "Something went wrong. Please try again.") {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;

  const detail = payload.detail;
  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first && typeof first.msg === "string" && first.msg.trim()) {
      const location = Array.isArray(first.loc) ? first.loc.slice(1).join(" > ") : "";
      return location ? `${location}: ${first.msg}` : first.msg;
    }
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string" && detail.msg.trim()) return detail.msg;
    const values = Object.values(detail).filter((value) => typeof value === "string" && value.trim());
    if (values.length > 0) return values[0];
  }

  if (typeof payload.message === "string" && payload.message.trim()) return payload.message;
  return fallback;
}

function getSelectedBookingDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const [timePart, meridiem] = timeValue.split(" ");
  if (!timePart || !meridiem) return null;
  const [hourPart, minutePart] = timePart.split(":");
  let hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  return new Date(
    dateValue.getFullYear(),
    dateValue.getMonth(),
    dateValue.getDate(),
    hours,
    minutes,
    0,
    0
  );
}

function normalizeEmailInput(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim().replace(/\s+/g, "");
  const [localPart, ...domainParts] = trimmed.split("@");
  if (!domainParts.length) {
    return trimmed;
  }
  const domain = domainParts.join("@").replace(EMAIL_QUOTE_CHARS_REGEX, "").toLowerCase();
  if (!domain && trimmed.endsWith("@")) {
    return `${localPart}@`;
  }
  return `${localPart}${domain ? `@${domain}` : ""}`;
}

export default function BookingModal({
  isOpen,
  onClose,
  preSelectedService,
  preSelectedCategoryId = "",
}) {
  const [step, setStep] = useState(1);
  const services = bookableServices;
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [selectedAddOnNotes, setSelectedAddOnNotes] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [bookingSettings, setBookingSettings] = useState(DEFAULT_BOOKING_SETTINGS);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    notes: "",
    id_document_name: "",
    id_document_data: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPoliciesModalOpen, setIsPoliciesModalOpen] = useState(false);
  const [hasApprovedPoliciesInModal, setHasApprovedPoliciesInModal] = useState(false);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const idUploadInputRef = useRef(null);
  const selectedTimeRef = useRef("");
  const minBookingNoticeHours = Number(bookingSettings?.min_notice_hours || MIN_BOOKING_NOTICE_HOURS);
  const minBookingDateTime = new Date(Date.now() + minBookingNoticeHours * 60 * 60 * 1000);
  const maxAdvanceDateTime = new Date(Date.now() + Number(bookingSettings?.max_advance_days || 120) * 24 * 60 * 60 * 1000);

  useEffect(() => {
    let isActive = true;
    const fetchBookingSettings = async () => {
      try {
        const res = await fetchApi("/availability/settings/public");
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !isActive) return;
        setBookingSettings((prev) => ({ ...prev, ...data }));
      } catch (error) {
        // Keep defaults if settings request fails.
      }
    };
    fetchBookingSettings();
    return () => {
      isActive = false;
    };
  }, []);

  const updateEmailValue = useCallback((nextValue) => {
    setFormData((current) => ({
      ...current,
      email: normalizeEmailInput(nextValue),
    }));
  }, []);

  useEffect(() => {
    selectedTimeRef.current = selectedTime;
  }, [selectedTime]);

  const applyPreselectedService = useCallback(() => {
    if (!preSelectedService || services.length === 0) return false;
    const service = services.find(
      (s) => s.id === preSelectedService || s.service_id === preSelectedService
    );
    if (!service) return false;
    setSelectedService(service);
    setStep(2);
    setSelectedAddOnIds([]);
    setSelectedAddOnNotes({});
    setCategoryFilter(service.categoryId || preSelectedCategoryId || "");
    return true;
  }, [preSelectedService, preSelectedCategoryId, services]);

  useEffect(() => {
    applyPreselectedService();
  }, [applyPreselectedService]);

  useEffect(() => {
    if (!isOpen) return;
    const hasApplied = applyPreselectedService();
    if (!hasApplied) {
      setCategoryFilter(preSelectedCategoryId || "");
    }
  }, [isOpen, preSelectedCategoryId, applyPreselectedService]);

  useEffect(() => {
    let isActive = true;

    const fetchAvailableTimes = async () => {
      if (!selectedDate || !selectedService) {
        setAvailableTimes([]);
        setAvailabilityMessage("");
        setSelectedTime("");
        return;
      }

      setIsLoadingTimes(true);
      setAvailabilityMessage("");
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const serviceMinutes = parseDurationMinutes(selectedService?.duration);
        const selectedAddOnMinutes = (selectedService?.addOns || [])
          .filter((addOn) => selectedAddOnIds.includes(addOn.id))
          .reduce((sum, addOn) => sum + parseDurationMinutes(addOn?.name), 0);
        const totalDurationMinutes = Math.max(30, serviceMinutes + selectedAddOnMinutes);
        const res = await fetchApi(
          `/available-times?date=${dateStr}&duration_minutes=${totalDurationMinutes}&service_name=${encodeURIComponent(selectedService?.name || "")}`
        );
        const data = await res.json().catch(() => ({}));
        if (!isActive) return;

        if (!res.ok) {
          setAvailableTimes([]);
          setAvailabilityMessage(
            extractApiErrorMessage(data, AVAILABILITY_FETCH_ERROR_MESSAGE)
          );
          setSelectedTime("");
          return;
        }

        const nextTimes = Array.isArray(data.available_times) ? data.available_times : [];
        setAvailableTimes(nextTimes);
        if (selectedTimeRef.current && !nextTimes.includes(selectedTimeRef.current)) {
          setSelectedTime("");
          setAvailabilityMessage("Your previously selected time is no longer available. Please choose another slot.");
        } else if (nextTimes.length === 0) {
          setAvailabilityMessage(NO_TIMES_AVAILABLE_MESSAGE);
        }
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch times:", error);
        setAvailableTimes([]);
        setSelectedTime("");
        setAvailabilityMessage(AVAILABILITY_FETCH_ERROR_MESSAGE);
      } finally {
        if (isActive) {
          setIsLoadingTimes(false);
        }
      }
    };
    fetchAvailableTimes();
    return () => {
      isActive = false;
    };
  }, [selectedDate, selectedService, selectedAddOnIds]);

  const getCurrentDurationMinutes = useCallback(() => {
    const serviceMinutes = parseDurationMinutes(selectedService?.duration);
    const selectedAddOnMinutes = (selectedService?.addOns || [])
      .filter((addOn) => selectedAddOnIds.includes(addOn.id))
      .reduce((sum, addOn) => sum + parseDurationMinutes(addOn?.name), 0);
    return Math.max(30, serviceMinutes + selectedAddOnMinutes);
  }, [selectedAddOnIds, selectedService]);

  const refreshAvailability = useCallback(async () => {
    if (!selectedDate || !selectedService) {
      return { ok: false, availableTimes: [], message: "Please choose a service and date first." };
    }

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const totalDurationMinutes = getCurrentDurationMinutes();
    const res = await fetchApi(
      `/available-times?date=${dateStr}&duration_minutes=${totalDurationMinutes}&service_name=${encodeURIComponent(selectedService?.name || "")}`
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        availableTimes: [],
        message: extractApiErrorMessage(data, AVAILABILITY_FETCH_ERROR_MESSAGE),
      };
    }
    return {
      ok: true,
      availableTimes: Array.isArray(data.available_times) ? data.available_times : [],
      message: "",
    };
  }, [getCurrentDurationMinutes, selectedDate, selectedService]);

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error("Please complete all booking details");
      return;
    }

    const normalizedEmail = normalizeEmailInput(formData.email);

    if (!formData.full_name || !normalizedEmail || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (normalizedEmail !== formData.email) {
      setFormData((current) => ({ ...current, email: normalizedEmail }));
    }

    if (!formData.id_document_name || !formData.id_document_data) {
      toast.error("Photo ID or driver's license upload is required");
      return;
    }

    if (!hasAcceptedPolicies) {
      toast.error("You must review and accept the salon policies before booking");
      return;
    }

    if (isLoadingTimes) {
      toast.error("Availability is still loading. Please wait a moment and try again.");
      return;
    }

    const bookingDateTime = getSelectedBookingDateTime(selectedDate, selectedTime);
    if (!bookingDateTime || bookingDateTime < minBookingDateTime) {
      setStep(3);
      toast.error("Bookings must be made at least 48 hours in advance.");
      return;
    }

    if (!availableTimes.includes(selectedTime)) {
      setSelectedTime("");
      setStep(3);
      setAvailabilityMessage("That time is no longer available. Please choose another slot.");
      toast.error("That time is no longer available. Please choose another slot.");
      return;
    }

    const selectedAddOns = (selectedService?.addOns || [])
      .filter((addOn) => selectedAddOnIds.includes(addOn.id))
      .map((addOn) => ({
        id: addOn.id,
        name: addOn.name,
        price: Number(addOn.price || 0),
        duration_minutes: parseDurationMinutes(addOn.name),
        note: selectedAddOnNotes[addOn.id] || "",
      }));
    const addonAmount = selectedAddOns.reduce((sum, addOn) => sum + Number(addOn.price || 0), 0);
    const serviceDurationMinutes = parseDurationMinutes(selectedService?.duration);
    const addOnDurationMinutes = selectedAddOns.reduce(
      (sum, addOn) => sum + Number(addOn.duration_minutes || 0),
      0
    );
    const totalDurationMinutes = Math.max(30, serviceDurationMinutes + addOnDurationMinutes);

    setIsSubmitting(true);
    try {
      const latestAvailability = await refreshAvailability();
      if (!latestAvailability.ok) {
        setStep(3);
        setAvailableTimes([]);
        setSelectedTime("");
        setAvailabilityMessage(latestAvailability.message);
        throw new Error(latestAvailability.message);
      }

      setAvailableTimes(latestAvailability.availableTimes);
      if (!latestAvailability.availableTimes.includes(selectedTime)) {
        setSelectedTime("");
        setStep(3);
        setAvailabilityMessage("That time was just taken. Please select a new time.");
        throw new Error("That time was just taken. Please select a new time.");
      }

      const bookingData = {
        service_id: selectedService.service_id || selectedService.id,
        service_name: selectedService.name,
        service_price: selectedService.price,
        service_duration: selectedService.duration,
        duration_minutes: totalDurationMinutes,
        selected_addons: selectedAddOns,
        addon_amount: addonAmount,
        full_name: formData.full_name,
        email: normalizedEmail,
        phone: formData.phone,
        preferred_date: format(selectedDate, "yyyy-MM-dd"),
        preferred_time: selectedTime,
        notes: formData.notes,
        id_document_name: formData.id_document_name,
        id_document_data: formData.id_document_data,
      };

      const checkoutEndpoints = [
        "/payments/checkout-session",
        "/payment/checkout-session",
        "/stripe/checkout-session",
      ];

      let data = null;
      let lastError = "Payment session failed";

      for (const endpoint of checkoutEndpoints) {
        const res = await fetchApi(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookingData),
        });
        if (res.ok) {
          data = await res.json();
          break;
        }
        const error = await res.json().catch(() => ({}));
        lastError = extractApiErrorMessage(
          error,
          `Payment session failed (${res.status})`
        );
        if (res.status !== 404) {
          break;
        }
      }

      if (!data?.checkout_url) throw new Error(lastError || "Missing Stripe checkout URL");
      toast.success("Redirecting to secure payment...");
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message || "Failed to start payment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedAddOnIds([]);
    setSelectedAddOnNotes({});
    setSelectedDate(null);
    setSelectedTime("");
    setAvailableTimes([]);
    setAvailabilityMessage("");
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      notes: "",
      id_document_name: "",
      id_document_data: "",
    });
    setIsPoliciesModalOpen(false);
    setHasApprovedPoliciesInModal(false);
    setHasAcceptedPolicies(false);
    setIsSuccess(false);
    setCategoryFilter("");
  };

  const displayedServices = categoryFilter
    ? services.filter((service) => service.categoryId === categoryFilter)
    : services;
  const availableAddOns = selectedService?.addOns || [];
  const selectedAddOns = availableAddOns.filter((addOn) => selectedAddOnIds.includes(addOn.id));
  const addOnTotal = selectedAddOns.reduce((sum, addOn) => sum + Number(addOn.price || 0), 0);
  const serviceDurationMinutes = parseDurationMinutes(selectedService?.duration);
  const addOnDurationMinutes = selectedAddOns.reduce(
    (sum, addOn) => sum + parseDurationMinutes(addOn?.name),
    0
  );
  const estimatedDurationMinutes = Math.max(30, serviceDurationMinutes + addOnDurationMinutes);
  const isDeluxeService = /deluxe/i.test(selectedService?.name || "");
  const lengthPattern = /\b(1[89]|2\d|3\d|40)\s*"/;
  const bundleLengthPattern = /\b(1[89]|2\d|3\d|40)-(1[89]|2\d|3\d|40)-(1[89]|2\d|3\d|40)(-(1[89]|2\d|3\d|40))?\b/;
  const lengthAddOnIds = availableAddOns
    .filter((addOn) => lengthPattern.test(addOn.name || "") || bundleLengthPattern.test(addOn.name || ""))
    .map((addOn) => addOn.id);
  const deluxeNeedsLengthSelection = isDeluxeService && lengthAddOnIds.length > 0;
  const hasSelectedRequiredLength = lengthAddOnIds.some((id) => selectedAddOnIds.includes(id));
  const selectedLengthAddOnId = selectedAddOnIds.find((id) => lengthAddOnIds.includes(id)) || null;
  const addOnConflictGroups = [
    ["straight", "body-wave", "deepwave", "loose-deep-wave"],
  ];

  const getConflictGroup = (addOnId) =>
    addOnConflictGroups.find((group) => group.includes(addOnId)) || null;

  const parsePriceNumber = (value) => {
    const match = String(value || "").match(/(\d+(?:\.\d{1,2})?)/);
    return match ? Number(match[1]) : 0;
  };
  const isHairLengthAddOn = (addOn) => {
    const name = String(addOn?.name || "");
    const id = String(addOn?.id || "");
    const haystack = `${name} ${id}`.toLowerCase();
    const hasQuotedLength = /\b(1[89]|2\d|3\d|40)\s*"/.test(haystack);
    const hasBundleLength = /\b(1[89]|2\d|3\d|40)(-(1[89]|2\d|3\d|40)){2,3}\b/.test(haystack);
    const hasLengthId = /\b(1[89]|2\d|3\d|40)[-_]?(?:inch|inches)\b/.test(haystack);
    const hasBundlePlusUnit = /\b\d+\s*(1[89]|2\d|3\d|40)\s*"\s*\+/.test(haystack);
    return hasQuotedLength || hasBundleLength || hasLengthId || hasBundlePlusUnit;
  };
  const serviceBasePrice = parsePriceNumber(selectedService?.price);
  const estimatedTotal = serviceBasePrice + addOnTotal;
  const baseDepositAmount = 20;
  const selectedHairLengthTotal = selectedAddOns.reduce((sum, addOn) => {
    const price = Number(addOn?.price || 0);
    if (isDeluxeService && (isHairLengthAddOn(addOn) || price >= 200)) {
      return sum + price;
    }
    return sum;
  }, 0);
  const dueNowAmount = Math.min(
    estimatedTotal,
    isDeluxeService
      ? serviceBasePrice * 0.5 + selectedHairLengthTotal * 0.5
      : baseDepositAmount
  );
  const remainingAfterUpfront = Math.max(estimatedTotal - dueNowAmount, 0);

  const toggleAddOn = (addOnId) => {
    setSelectedAddOnIds((prev) => {
      const isSelected = prev.includes(addOnId);
      const isLengthAddOn = isDeluxeService && lengthAddOnIds.includes(addOnId);

      if (isSelected) {
        return prev.filter((id) => id !== addOnId);
      }

      // Deluxe rule: only one hair-length add-on can be selected at a time.
      if (isLengthAddOn) {
        const withoutOtherLengths = prev.filter((id) => !lengthAddOnIds.includes(id));
        return [...withoutOtherLengths, addOnId];
      }

      // General compatibility rule: only one option per conflict group.
      const conflictGroup = getConflictGroup(addOnId);
      if (conflictGroup) {
        const withoutConflictGroup = prev.filter((id) => !conflictGroup.includes(id));
        return [...withoutConflictGroup, addOnId];
      }

      return [...prev, addOnId];
    });
  };

  const handleIdUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isImage && !isPdf) {
      toast.error("Please upload an image or PDF file for ID");
      event.target.value = "";
      return;
    }

    const maxSizeBytes = 8 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error("ID file is too large. Max size is 8MB.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) {
        toast.error("Failed to read the selected file.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        id_document_name: file.name,
        id_document_data: dataUrl,
      }));
    };
    reader.onerror = () => {
      toast.error("Failed to read the selected file.");
    };
    reader.readAsDataURL(file);
  };

  const openIdPicker = () => {
    idUploadInputRef.current?.click();
  };

  const removeUploadedId = () => {
    setFormData((prev) => ({
      ...prev,
      id_document_name: "",
      id_document_data: "",
    }));
    if (idUploadInputRef.current) {
      idUploadInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
              step >= s
                ? "bg-[#111111] text-white"
                : "bg-[#f0f0f0] text-[#888]"
            }`}
          >
            {step > s ? <Check size={14} /> : s}
          </div>
          {s < 4 && (
            <div
              className={`w-10 h-0.5 ${
                step > s ? "bg-[#111111]" : "bg-[#e0e0e0]"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-xl max-h-[90vh] overflow-y-auto p-0 bg-white rounded-2xl"
      >
        {isSuccess ? (
          <div className="p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-600" size={32} />
            </div>
            <h3 className="script-heading text-2xl mb-2">Booking Confirmed!</h3>
            <p className="text-[#888] mb-6">
              Thank you for booking with DadaDeLuxe. We'll send a confirmation email shortly.
            </p>
            <div className="bg-[#f5f5f5] rounded-xl p-4 mb-6 text-left text-sm">
              <p><strong>Service:</strong> {selectedService?.name}</p>
              <p><strong>Date:</strong> {selectedDate && format(selectedDate, "MMMM d, yyyy")}</p>
              <p><strong>Time:</strong> {selectedTime}</p>
            </div>
            <button
              data-testid="close-booking-success"
              onClick={handleClose}
              className="btn-black"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle className="script-heading text-2xl text-center">
                Book Your Appointment
              </DialogTitle>
            </DialogHeader>

            <div className="p-6">
              <StepIndicator />

              {/* Step 1: Select Service */}
              {step === 1 && (
                <div className="animate-fade-in" data-testid="booking-step-1">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarIcon size={16} className="text-[#888]" />
                    <span className="text-sm text-[#888]">Select Service</span>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {displayedServices.length === 0 ? (
                      <div className="rounded-xl border border-[#e0e0e0] p-4 text-sm text-[#666]">
                        No services found for this category.
                      </div>
                    ) : (
                      displayedServices.map((service) => (
                        <button
                          key={service.id}
                          data-testid={`service-option-${service.id}`}
                          onClick={() => {
                            setSelectedService(service);
                            setSelectedAddOnIds([]);
                            setSelectedAddOnNotes({});
                          }}
                          className={`w-full p-4 rounded-xl border text-left transition-all ${
                            selectedService?.id === service.id
                              ? "border-[#111111] bg-[#f5f5f5]"
                              : "border-[#e0e0e0] hover:border-[#888]"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{service.name}</p>
                              <p className="text-sm text-[#888]">{service.categoryName} • {service.duration}</p>
                            </div>
                            <p className="text-sm font-medium">{service.price}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      data-testid="booking-next-1"
                      onClick={() => selectedService && setStep(2)}
                      disabled={!selectedService}
                      className="btn-black flex items-center gap-2 disabled:opacity-50"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Add-Ons */}
              {step === 2 && (
                <div className="animate-fade-in" data-testid="booking-step-2">
                  <div className="flex items-center gap-2 mb-4">
                    <Check size={16} className="text-[#888]" />
                    <span className="text-sm text-[#888]">Select Add-Ons</span>
                  </div>

                  {availableAddOns.length === 0 ? (
                    <div className="rounded-xl border border-[#e0e0e0] p-4 text-sm text-[#666]">
                      No add-ons for this service yet.
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[320px] overflow-y-auto">
                      {availableAddOns.map((addOn) => {
                        const isSelected = selectedAddOnIds.includes(addOn.id);
                        const isLengthAddOn = isDeluxeService && lengthAddOnIds.includes(addOn.id);
                        const isDisabledLengthOption =
                          isLengthAddOn &&
                          selectedLengthAddOnId &&
                          selectedLengthAddOnId !== addOn.id;
                        return (
                          <div
                            key={addOn.id}
                            className={`rounded-xl border p-4 transition-all ${
                              isSelected ? "border-[#111111] bg-[#f5f5f5]" : "border-[#e0e0e0]"
                            }`}
                          >
                            <label
                              className={`flex items-start gap-3 ${
                                isDisabledLengthOption ? "cursor-not-allowed opacity-55" : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleAddOn(addOn.id)}
                                disabled={isDisabledLengthOption}
                                className="mt-1 h-4 w-4 accent-[#111111]"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="font-medium text-[#111]">{addOn.name}</p>
                                  <p className="text-sm font-medium text-[#111]">
                                    {Number(addOn.price || 0) > 0
                                      ? `+$${Number(addOn.price).toFixed(2)}`
                                      : Number(addOn.price || 0) < 0
                                        ? `-$${Math.abs(Number(addOn.price)).toFixed(2)}`
                                        : "Included"}
                                  </p>
                                </div>
                                {isSelected && addOn.requiresText && (
                                  <div className="mt-3">
                                    <Label className="text-[11px] text-[#777]">
                                      {addOn.textLabel || "Add details"}
                                    </Label>
                                    <Input
                                      value={selectedAddOnNotes[addOn.id] || ""}
                                      onChange={(e) =>
                                        setSelectedAddOnNotes((prev) => ({
                                          ...prev,
                                          [addOn.id]: e.target.value,
                                        }))
                                      }
                                      className="mt-1 luxury-input"
                                      placeholder={addOn.textPlaceholder || "Type details"}
                                    />
                                  </div>
                                )}
                              </div>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-4 rounded-xl bg-[#f5f5f5] p-3 text-sm">
                    <p><span className="text-[#888]">Service Price:</span> ${serviceBasePrice.toFixed(2)}</p>
                    <p><span className="text-[#888]">Add-Ons:</span> ${addOnTotal.toFixed(2)}</p>
                    <p className="font-medium"><span className="text-[#888]">Estimated Total:</span> ${estimatedTotal.toFixed(2)}</p>
                    <p><span className="text-[#888]">Estimated Duration:</span> {estimatedDurationMinutes} min</p>
                  </div>
                  {deluxeNeedsLengthSelection && !hasSelectedRequiredLength && (
                    <p className="mt-3 text-sm text-[#b42318]">
                      Deluxe services require one hair-length selection before continuing.
                    </p>
                  )}

                  <div className="mt-6 flex justify-between">
                    <button
                      data-testid="booking-back-2"
                      onClick={() => setStep(1)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      data-testid="booking-next-2"
                      onClick={() => {
                        if (deluxeNeedsLengthSelection && !hasSelectedRequiredLength) {
                          toast.error("Please select a hair length add-on for this deluxe service");
                          return;
                        }
                        setStep(3);
                      }}
                      className="btn-black flex items-center gap-2 disabled:opacity-50"
                      disabled={deluxeNeedsLengthSelection && !hasSelectedRequiredLength}
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Select Date & Time */}
              {step === 3 && (
                <div className="animate-fade-in" data-testid="booking-step-3">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={16} className="text-[#888]" />
                    <span className="text-sm text-[#888]">Select Date & Time</span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-[#888] mb-2 block">Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                          setSelectedTime("");
                          setAvailabilityMessage("");
                        }}
                        disabled={(date) => {
                          const startOfSelectedDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                          const startOfToday = new Date();
                          startOfToday.setHours(0, 0, 0, 0);
                          const cutoffDayStart = new Date(
                            minBookingDateTime.getFullYear(),
                            minBookingDateTime.getMonth(),
                            minBookingDateTime.getDate()
                          );
                          const maxAdvanceDayStart = new Date(
                            maxAdvanceDateTime.getFullYear(),
                            maxAdvanceDateTime.getMonth(),
                            maxAdvanceDateTime.getDate()
                          );
                          const dayKey = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][date.getDay()];
                          const daySettings = (bookingSettings?.weekly_hours || []).find((day) => day.day_key === dayKey);
                          return (
                            startOfSelectedDay < startOfToday ||
                            startOfSelectedDay < cutoffDayStart ||
                            startOfSelectedDay > maxAdvanceDayStart ||
                            daySettings?.enabled === false
                          );
                        }}
                        className="rounded-xl border border-[#e0e0e0] p-3 bg-white"
                        data-testid="booking-calendar"
                      />
                      <p className="text-[11px] text-[#888] mt-2">
                        Bookings must be made at least {minBookingNoticeHours} hours in advance.
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs text-[#888] mb-2 block">Time</Label>
                      {isSewInService(selectedService?.name) && (
                        <p className="text-[11px] text-[#a33e31] mb-2" data-testid="sew-in-cutoff-note">
                          Sew-ins cannot be booked after 3:00 PM.
                        </p>
                      )}
                      {!selectedDate ? (
                        <p className="text-sm text-[#888]">Select a date first</p>
                      ) : isLoadingTimes ? (
                        <p className="text-sm text-[#888]">Loading...</p>
                      ) : availabilityMessage ? (
                        <p className="text-sm text-[#888]">{availabilityMessage}</p>
                      ) : availableTimes.length === 0 ? (
                        <p className="text-sm text-[#888]">{NO_TIMES_AVAILABLE_MESSAGE}</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
                          {availableTimes.map((time) => (
                            <button
                              key={time}
                              data-testid={`time-slot-${time.replace(/[:\s]/g, "")}`}
                              onClick={() => setSelectedTime(time)}
                              className={`p-3 rounded-lg border text-sm transition-all ${
                                selectedTime === time
                                  ? "border-[#111111] bg-[#111111] text-white"
                                  : "border-[#e0e0e0] hover:border-[#888]"
                              }`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button
                      data-testid="booking-back-3"
                      onClick={() => setStep(2)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      data-testid="booking-next-3"
                      onClick={() => selectedDate && selectedTime && setStep(4)}
                      disabled={!selectedDate || !selectedTime}
                      className="btn-black flex items-center gap-2 disabled:opacity-50"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Contact Info */}
              {step === 4 && (
                <div className="animate-fade-in" data-testid="booking-step-4">
                  <div className="flex items-center gap-2 mb-4">
                    <User size={16} className="text-[#888]" />
                    <span className="text-sm text-[#888]">Your Information</span>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="full_name" className="text-xs text-[#888]">
                        Full Name *
                      </Label>
                      <Input
                        id="full_name"
                        data-testid="booking-name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        autoComplete="name"
                        className="mt-1 luxury-input"
                        placeholder="Jane Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-xs text-[#888]">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="text"
                        data-testid="booking-email"
                        value={formData.email}
                        onChange={(e) => updateEmailValue(e.target.value)}
                        onBlur={(e) => updateEmailValue(e.target.value)}
                        autoComplete="email"
                        inputMode="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        enterKeyHint="next"
                        className="mt-1 luxury-input"
                        placeholder="jane@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-xs text-[#888]">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="booking-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        autoComplete="tel"
                        inputMode="tel"
                        className="mt-1 luxury-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <Label htmlFor="id_document" className="text-xs text-[#888]">
                        Upload Photo ID or Driver's License *
                      </Label>
                      <input
                        id="id_document"
                        ref={idUploadInputRef}
                        type="file"
                        accept="image/*,.pdf,application/pdf"
                        data-testid="booking-id-upload"
                        onChange={handleIdUpload}
                        className="hidden"
                      />
                      <div className="mt-2 rounded-xl border border-[#d8c3b0] bg-[#fbf9f7] p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-widest text-[#888]">
                              Required Verification
                            </p>
                            <p className="text-sm text-[#555] truncate">
                              {formData.id_document_name || "No file selected"}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={openIdPicker}
                            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-4 py-2 text-xs tracking-wide hover:bg-[#333333] transition-colors"
                          >
                            <Upload size={14} />
                            {formData.id_document_name ? "Replace" : "Upload"}
                          </button>
                        </div>
                        <p className="mt-2 text-[11px] text-[#888]">
                          Accepted: JPG, PNG, and PDF. Max size: 8MB.
                        </p>
                        {formData.id_document_name && (
                          <button
                            type="button"
                            onClick={removeUploadedId}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-[#666] hover:text-[#111]"
                          >
                            <Trash2 size={12} />
                            Remove file
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-red-600 font-bold">MUST VIEW POLICIES BEFORE BOOKING! *</Label>
                        <button
                          type="button"
                          data-testid="booking-view-policies"
                          onClick={() => {
                            setIsPoliciesModalOpen(true);
                          }}
                          className="text-xs underline underline-offset-4 text-[#111] hover:text-[#666]"
                        >
                          View Policies
                        </button>
                      </div>
                      <div className="mt-2 flex items-start gap-2">
                        <input
                          id="booking-policy-accept"
                          data-testid="booking-policy-checkbox"
                          type="checkbox"
                          checked={hasAcceptedPolicies}
                          disabled={!hasApprovedPoliciesInModal}
                          onChange={(e) => setHasAcceptedPolicies(e.target.checked)}
                          className="mt-0.5 h-4 w-4 accent-[#111111] disabled:opacity-40"
                        />
                        <Label
                          htmlFor="booking-policy-accept"
                          className="text-xs text-[#666] leading-relaxed"
                        >
                          I have read and agree to the salon policies.
                          {!hasApprovedPoliciesInModal && " Please click \"I Have Read & Agree\" in Policies first."}
                        </Label>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-xs text-[#888]">
                        Notes (optional)
                      </Label>
                      <Textarea
                        id="notes"
                        data-testid="booking-notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="mt-1 luxury-input min-h-[80px]"
                        placeholder="Any special requests..."
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-6 p-4 bg-[#f5f5f5] rounded-xl">
                    <p className="text-xs text-[#888] mb-2">Booking Summary</p>
                    <div className="text-sm space-y-1">
                      <p><span className="text-[#888]">Service:</span> {selectedService?.name}</p>
                      <p><span className="text-[#888]">Date:</span> {selectedDate && format(selectedDate, "MMMM d, yyyy")}</p>
                      <p><span className="text-[#888]">Time:</span> {selectedTime}</p>
                      <p><span className="text-[#888]">Service Price:</span> ${serviceBasePrice.toFixed(2)}</p>
                      <p><span className="text-[#888]">Add-Ons:</span> ${addOnTotal.toFixed(2)}</p>
                      {selectedAddOns.length > 0 && (
                        <p>
                          <span className="text-[#888]">Selected Add-Ons:</span>{" "}
                          {selectedAddOns
                            .map((addOn) =>
                              addOn.note ? `${addOn.name} (${addOn.note})` : addOn.name
                            )
                            .join(", ")}
                        </p>
                      )}
                      <p><span className="text-[#888]">Estimated Total:</span> ${estimatedTotal.toFixed(2)}</p>
                      <p><span className="text-[#888]">Due Now (Upfront):</span> ${dueNowAmount.toFixed(2)}</p>
                      <p><span className="text-[#888]">Remaining Balance:</span> ${remainingAfterUpfront.toFixed(2)}</p>
                      <p><span className="text-[#888]">ID Uploaded:</span> {formData.id_document_name ? "Yes" : "No"}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button
                      data-testid="booking-back-4"
                      onClick={() => setStep(3)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      data-testid="booking-submit"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !hasAcceptedPolicies}
                      className="btn-black flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner" /> Redirecting...
                        </>
                      ) : (
                        `Pay $${dueNowAmount.toFixed(2)} Upfront`
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {isPoliciesModalOpen && (
              <div className="fixed inset-0 z-[120] bg-black/50 p-4 flex items-center justify-center">
                <div className="w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl border border-[#e8e8e8]">
                  <div className="px-5 py-4 border-b border-[#ececec] flex items-center justify-between">
                    <h3 className="script-heading text-2xl">Salon Policies</h3>
                    <button
                      type="button"
                      onClick={() => setIsPoliciesModalOpen(false)}
                      className="p-1.5 rounded-full hover:bg-[#f5f5f5] text-[#666]"
                      aria-label="Close policies modal"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="p-5 space-y-3 overflow-y-auto max-h-[58vh] bg-[#fafafa]">
                    {SALON_POLICIES.map((policy) => (
                      <div key={policy.id} className="bg-white rounded-xl border border-[#ececec] p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={14} className="text-[#666]" />
                          <p className="font-medium text-[#111]">{policy.title}</p>
                        </div>
                        <p className="text-sm text-[#666] whitespace-pre-line leading-relaxed">
                          {policy.content}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="px-5 py-4 border-t border-[#ececec] flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setIsPoliciesModalOpen(false)}
                      className="btn-outline"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setHasApprovedPoliciesInModal(true);
                        setHasAcceptedPolicies(true);
                        setIsPoliciesModalOpen(false);
                      }}
                      className="btn-black"
                    >
                      I Have Read & Agree
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
